import { useState } from "react";
import { Link } from "react-router-dom";
import { QuickView } from "@/components/common/QuickView";
import { ArrowLeft, Inbox, Loader2, Search } from "lucide-react";
import { tone } from "@/lib/format";

export const StatusBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone(status)}`}>{status || "—"}</span>
);

export function PageHeader({ title, subtitle, actions, back }) {
  return (
    <div className="mb-6 flex flex-col gap-4 2xl:flex-row 2xl:items-end 2xl:justify-between fade-up">
      <div className="min-w-0">
        {back && (
          <Link to={back} className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-blue-700" data-testid="page-back-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver
          </Link>
        )}
        <h1 className="break-words text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="page-title">{title}</h1>
        <span className="mt-2 block h-1 w-12 rounded-full bg-gradient-to-r from-blue-600 to-orange-500" />
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export const Loading = () => (
  <div className="flex items-center gap-2 p-8 text-sm text-slate-500" data-testid="loading"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
);

export const EmptyState = ({ text = "No hay registros todavía." }) => (
  <div className="flex flex-col items-center gap-2 p-10 text-center text-sm text-slate-400" data-testid="empty-state"><Inbox className="h-6 w-6" />{text}</div>
);

export const ErrorBox = ({ text }) => text ? <div className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" role="alert" data-testid="error-box">{text}</div> : null;

export function Section({ title, actions, children, className = "", testid }) {
  return (
    <section className={`card-box min-w-0 p-5 ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="mb-4 flex items-center justify-between gap-2">
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function KV({ items }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{k}</dt>
          <dd className="mt-0.5 break-words text-sm text-slate-800">{v || v === 0 ? v : "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function FilterBar({ search, onSearch, filters = [], values = {}, onChange, testid = "filters", children }) {
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center" data-testid={testid}>
      {onSearch && (
        <div className="relative sm:w-72">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input className="field-input pl-9" placeholder="Buscar…" value={search} onChange={(e) => onSearch(e.target.value)} data-testid={`${testid}-search`} />
        </div>
      )}
      {filters.map((f) => (
        <select key={f.key} className="field-input sm:w-52" value={values[f.key] || ""} onChange={(e) => onChange({ ...values, [f.key]: e.target.value })}
          data-testid={`${testid}-${f.key}`} aria-label={f.label}>
          <option value="">{f.label}: todos</option>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ))}
      {children}
    </div>
  );
}

export function DataTable({ columns, rows, onRowClick, loading, empty, testid = "table", quickView }) {
  const [peek, setPeek] = useState(null);
  if (loading && !rows) return <div className="card-box"><Loading /></div>;
  const click = onRowClick && (quickView ? (r) => setPeek(r) : onRowClick);
  return (
    <div className="card-box fade-up overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>{columns.map((c) => <th key={c.key} className={`whitespace-nowrap px-4 py-3 ${c.className || ""}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(rows || []).map((r) => (
              <tr key={r.id} onClick={click ? () => click(r) : undefined} data-testid={`${testid}-row-${r.id}`}
                className={`transition-colors duration-150 ${click ? "cursor-pointer hover:bg-blue-50/60" : ""}`}>
                {columns.map((c) => <td key={c.key} className={`px-4 py-3 align-top text-slate-700 ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows && rows.length === 0 && <EmptyState text={empty} />}
      {quickView && <QuickView row={peek} columns={columns} title={quickView} testid={testid} onClose={() => setPeek(null)} onOpenFull={(r) => { setPeek(null); onRowClick(r); }} />}
    </div>
  );
}
